import { AUTH_API_BASE, GUEST_AUTH_PHONE } from "@/config/apiConfig";

/**
 * Guest authentication for public pages.
 *
 * Unauthenticated visitors still need a bearer token to call protected APIs (e.g. the
 * fare engine). We mint a short-lived token on demand and keep it ONLY in memory — it is
 * deliberately never written to cookies/localStorage, so it cannot be picked up by the
 * auth flow to unlock private routes. It lives for the page session and is discarded on
 * reload.
 */

interface GuestTokenResponse {
  accessToken?: string;
  refreshToken?: string;
}

/** Refresh this many milliseconds before the token's real expiry to avoid races. */
const EXPIRY_SKEW_MS = 60_000;

let cachedToken: string | null = null;
let inflightRequest: Promise<string> | null = null;

/** Reads the `exp` claim (seconds) from a JWT without verifying its signature. */
function getTokenExpiryMs(token: string): number | null {
  const segments = token.split(".");
  if (segments.length < 2) return null;
  try {
    const payload = JSON.parse(atob(segments[1])) as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isTokenUsable(token: string | null): token is string {
  if (!token) return false;
  const expiryMs = getTokenExpiryMs(token);
  // If we can't read an expiry, treat it as usable until a 401 forces a refresh.
  if (expiryMs == null) return true;
  return expiryMs - Date.now() > EXPIRY_SKEW_MS;
}

async function requestGuestToken(): Promise<string> {
  const url = `${AUTH_API_BASE}/api/test/show-token?phone=${encodeURIComponent(
    GUEST_AUTH_PHONE
  )}`;

  const res = await fetch(url, { headers: { accept: "*/*" } });
  if (!res.ok) {
    throw new Error(`Guest token request failed: ${res.status}`);
  }

  const data = (await res.json()) as GuestTokenResponse;
  if (!data.accessToken) {
    throw new Error("Guest token response did not include an access token");
  }

  return data.accessToken;
}

/**
 * Returns a valid in-memory guest access token, fetching one if needed. Concurrent
 * callers share a single in-flight request.
 */
export async function getGuestAccessToken(): Promise<string> {
  if (isTokenUsable(cachedToken)) return cachedToken;
  if (inflightRequest) return inflightRequest;

  inflightRequest = requestGuestToken()
    .then((token) => {
      cachedToken = token;
      return token;
    })
    .finally(() => {
      inflightRequest = null;
    });

  return inflightRequest;
}

/** Drops the cached token so the next call mints a fresh one (e.g. after a 401). */
export function invalidateGuestToken(): void {
  cachedToken = null;
}
